import constants from '@Common/Core/Constant';
import { DocumentBase, DocumentDescriptor } from '@InstanceFactory';

import documentUtils from '../Helpers/DocumentUtils';
import Guid from '@Common/Core/Guid';

const uniqueCategories = new Set<string>([
    constants.documentCategories.metricPool,
    constants.documentCategories.metricDerived,
    constants.documentCategories.metricProcess,
    constants.documentCategories.olapDocument,
    constants.documentCategories.metricRegression,
    constants.documentCategories.metric
]);

export class DocumentNameProvider {
    /** projectId: {
            category1: {
                document_name1: {
                    document_id1: DocumentDescriptor
                }
            },
            category2: {
                document_name2: {
                    document_id2: DocumentDescriptor
                },
                document_name3: {
                    document_id3: DocumentDescriptor,
                    document_id4: DocumentDescriptor
                }
            }
        }
     */
    private documentNames: IMap<IMap<IMap<IMap<DocumentDescriptor>>>> = {};

    public init(descriptors: DocumentDescriptor[], projectId: Guid): void {
        this.documentNames = { ...this.documentNames, [projectId]: {} };
        descriptors.forEach(d => this.add(d, projectId));
    }

    public getExistedNames(category: string, { projectId }: { projectId: Guid }, parentId?: string): string[] {
        const byNames = this.getNames(category, projectId);
        if (this.uniqueNameInFolder(category)) {
            return _(byNames)
                .flatMap(names => _.values(names))
                .filter({ parentId })
                .map(desc => desc.name)
                .value();
        } else {
            return Object.keys(byNames);
        }
    }

    public existName(category: string, name: string, projectId: Guid): boolean {
        const byNames = this.getNames(category, projectId);
        return byNames[name.toLowerCase()] != null;
    }

    public add(doc: DocumentBase | DocumentDescriptor, projectId: Guid): void {
        const desc = this.getDesc(doc);
        const byNames = this.getNames(desc.category, projectId);
        let byIds = byNames[desc.name.toLowerCase()];
        if (!byIds || !this.uniqueNameInFolder(desc.category)) {
            byIds = {};
            byNames[desc.name.toLowerCase()] = byIds;
        }
        byIds[desc.id] = desc;
    }

    public rename(desc: DocumentDescriptor, projectId: Guid): void {
        this.refresh(desc, projectId);
    }

    public remove(doc: DocumentBase | DocumentDescriptor, projectId: Guid): void {
        const desc = this.getDesc(doc);
        const byNames = this.getNames(desc.category, projectId);
        const name = _(byNames)
            .pickBy(byIds => byIds.hasOwnProperty(desc.id))
            .keys()
            .first();
        if (name) {
            const nameObj = byNames[name];
            if (Object.keys(nameObj).length === 1) {
                delete byNames[name];
            } else {
                delete nameObj[desc.id];
            }
        }
    }

    public refresh(doc: DocumentBase | DocumentDescriptor, projectId: Guid): void {
        this.remove(doc, projectId);
        this.add(doc, projectId);
    }

    public isDuplicate(doc: DocumentBase | DocumentDescriptor, projectId: Guid): boolean {
        const desc: DocumentDescriptor = this.getDesc(doc);
        const byNames = this.getNames(desc.category, projectId);
        const name = desc.name.toLowerCase();
        if (!byNames[name]) {
            return false;
        }

        const byIds = byNames[name];
        if (this.uniqueNameInFolder(desc.category)) {
            return _.some(byIds, (item, id) => id !== desc.id && item.parentId === desc.parentId);
        } else {
            return Object.keys(byIds)[0] !== desc.id;
        }
    }

    private getNames(category: string, projectId: Guid): IMap<IMap<DocumentDescriptor>> {
        const internalCategory = this.getInternalCategory(category);
        const projectDocuments = (this.documentNames[projectId] = this.documentNames[projectId] ?? {});
        return (projectDocuments[internalCategory] = projectDocuments[internalCategory] ?? {});
    }

    private getInternalCategory(category): string {
        return uniqueCategories.has(category) ? '__uniqueCategory' : category;
    }

    private uniqueNameInFolder(category): boolean {
        return category === constants.documentCategories.view;
    }

    private getDesc(doc: DocumentBase | DocumentDescriptor): DocumentDescriptor {
        return {
            id: doc.id,
            name: doc.name,
            category: _.get(doc, 'category') || documentUtils.getDocumentCategory(doc),
            parentId: doc.parentId,
            attributes: undefined,
            ownerId: undefined,
            isNew: undefined,
            lastChangedTime: undefined,
            documentDescription: undefined
        };
    }
}

const documentNameProvider = new DocumentNameProvider();
export default documentNameProvider;
